import React, { useState } from "react";
import { Plus } from "lucide-react";
import type { PersonDetailWire } from "@releve/shared";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { PersonFormDialog } from "./person-form/PersonFormDialog";

interface PersonCreateDialogProps {
  /** Uncontrolled use: this element opens the dialog (its own onClick still runs). */
  trigger?: React.ReactElement<{ onClick?: (e: React.MouseEvent) => void }>;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Called with the created person. */
  onSuccess?: (person: PersonDetailWire) => void;
  /** Person types pre-selected in the form. */
  defaultPersonTypes?: string[];
  /** The form tab to open on. */
  defaultTab?: string;
  /** On create, continue to the new person's page (default true). Pass false for create-to-select flows. */
  navigateOnCreate?: boolean;
}

/**
 * Create a person — the thin create wrapper over `PersonFormDialog`. Editing
 * goes through `PersonEditHost` (from an index row) or `PersonFormDialog`
 * directly (the detail page, which already holds the full person).
 *
 * Controlled (`open` / `onOpenChange`) or uncontrolled with a `trigger`. The
 * form, and its hooks, mount only while open.
 */
export function PersonCreateDialog({
  trigger,
  open: controlledOpen,
  onOpenChange,
  onSuccess,
  defaultPersonTypes,
  defaultTab,
  navigateOnCreate = true,
}: PersonCreateDialogProps) {
  const { t } = useTranslation("people");
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;

  return (
    <>
      {controlledOpen === undefined &&
        (trigger ? (
          React.cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              trigger.props.onClick?.(e);
              setOpen(true);
            },
          })
        ) : (
          <Button onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t("page.create")}
          </Button>
        ))}

      {open && (
        <PersonFormDialog
          open={open}
          onOpenChange={setOpen}
          onSuccess={onSuccess}
          defaultPersonTypes={defaultPersonTypes}
          defaultTab={defaultTab}
          navigateOnCreate={navigateOnCreate}
        />
      )}
    </>
  );
}
